import { type ReactNode } from 'react';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { separatorShadow } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import { type QuillEditorOptions } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';
import useOnEscape from 'hb-react/shared/hooks/useOnEscape';

import {
  Headers,
  Bold,
  Italic,
  Underline,
  Color,
  ListOrdered,
  ListBullet,
  InlineImage,
  Image,
  Video,
  InlineVideo,
  Attachment,
  Link,
  Mentions,
  EmojiPicker,
  TextAlign,
  Divider,
  Indent,
  CustomVariables,
  BackgroundColor,
  FontSize,
  Font,
} from './Buttons';
import { Separator } from './Separator';
import { useQuillEditorContext } from '../contexts/QuillEditorContext';

export { ACCEPT_ATTACHMENTS } from './Buttons/Attachment';
export { ACCEPT_IMAGES } from './Buttons/Image';
export { ACCEPT_VIDEOS } from './Buttons/Video';

interface GetToolbarControlsParams {
  toolbarOptions?: QuillEditorOptions['toolbar'];
}

const getToolbarControls = (enabled: any, Component: ReactNode) =>
  enabled ? Component : null;

const getToolbarControlsGroup1 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const {
    header: canDisplayHeader,
    fontSize: canDisplayFontSize,
    font: canDisplayFont,
  } = toolbarOptions;

  return [
    getToolbarControls(canDisplayHeader, <Headers key="headers" />),
    getToolbarControls(canDisplayFont, <Font key="font" />),
    getToolbarControls(canDisplayFontSize, <FontSize key="font-size" />),
  ].filter(Boolean);
};

const getToolbarControlsGroup2 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const {
    bold: canDisplayBold,
    italic: canDisplayItalic,
    underline: canDisplayUnderline,
  } = toolbarOptions;

  return [
    getToolbarControls(canDisplayBold, <Bold key="bold" />),
    getToolbarControls(canDisplayItalic, <Italic key="italic" />),
    getToolbarControls(canDisplayUnderline, <Underline key="underline" />),
  ].filter(Boolean);
};

const getToolbarControlsGroup3 = ({
  toolbarOptions = {},
  focusItem,
  handleClickItem,
}: GetToolbarControlsParams & {
  focusItem: string;
  handleClickItem: (string) => void;
}) => {
  const { color: canDisplayColor, backgroundColor: canDisplayBackgroundColor } =
    toolbarOptions;

  return [
    getToolbarControls(
      canDisplayColor,
      <Color
        key="color"
        isItemFocus={focusItem === 'Color'}
        onItemClick={() => handleClickItem('Color')}
        position="top"
      />,
    ),
    getToolbarControls(
      canDisplayBackgroundColor,
      <BackgroundColor
        key="backgroundColor"
        isItemFocus={focusItem === 'BackgroundColor'}
        onItemClick={() => handleClickItem('BackgroundColor')}
        position="top"
      />,
    ),
  ].filter(Boolean);
};

const getToolbarControlsGroup4 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const {
    list_bullet: canDisplayListBullet,
    list_ordered: canDisplayListOrdered,
  } = toolbarOptions;

  return [
    getToolbarControls(canDisplayListBullet, <ListBullet key="list-bullet" />),
    getToolbarControls(
      canDisplayListOrdered,
      <ListOrdered key="list-numbered" />,
    ),
  ].filter(Boolean);
};

const getToolbarControlsGroup5 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const { textAlignment: canDisplayTextAlignment } = toolbarOptions;

  return [
    getToolbarControls(canDisplayTextAlignment, <TextAlign key="text-align" />),
  ].filter(Boolean);
};

const getToolbarControlsGroup6 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const { indent: canDisplayIndent } = toolbarOptions;

  return [getToolbarControls(canDisplayIndent, <Indent key="indent" />)].filter(
    Boolean,
  );
};

const getToolbarControlsGroup7 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const {
    inlineImage: canDisplayInlineImage,
    images: canDisplayImages,
    video: canDisplayVideo,
    inlineVideo: canDisplayInLineVideo,
    attachments: canDisplayAttachments,
    links: canDisplayLinks,
  } = toolbarOptions;

  return [
    getToolbarControls(
      canDisplayInlineImage,
      <InlineImage key="inline-image" />,
    ),
    getToolbarControls(canDisplayImages, <Image key="image" />),
    getToolbarControls(canDisplayVideo, <Video key="video" />),
    getToolbarControls(
      canDisplayInLineVideo,
      <InlineVideo key="inline-video" />,
    ),
    getToolbarControls(canDisplayAttachments, <Attachment key="attachment" />),
    getToolbarControls(canDisplayLinks, <Link key="link" />),
  ].filter(Boolean);
};

const getToolbarControlsGroup8 = ({
  toolbarOptions = {},
}: GetToolbarControlsParams) => {
  const {
    emojiPicker: canDisplayEmojiPicker,
    mentions: canDisplayMentions,
    divider: canDisplayDivider,
    customVariables: canDisplayCustomVariables,
  } = toolbarOptions;

  return [
    getToolbarControls(canDisplayDivider, <Divider key="divider" />),
    getToolbarControls(canDisplayMentions, <Mentions key="mentions" />),
    getToolbarControls(canDisplayEmojiPicker, <EmojiPicker key="emoji" />),
    getToolbarControls(
      canDisplayCustomVariables,
      <CustomVariables
        variables={toolbarOptions?.customVariables?.variables}
        key="custom-variables"
      />,
    ),
  ].filter(Boolean);
};

const ItemsByOptions = ({ focusItem, handleClickItem }) => {
  const {
    options: { toolbar: toolbarOptions = {} },
  } = useQuillEditorContext();

  const group1 = getToolbarControlsGroup1({ toolbarOptions });
  const group2 = getToolbarControlsGroup2({ toolbarOptions });
  const group3 = getToolbarControlsGroup3({
    toolbarOptions,
    focusItem,
    handleClickItem,
  });
  const group4 = getToolbarControlsGroup4({ toolbarOptions });
  const group5 = getToolbarControlsGroup5({ toolbarOptions });
  const group6 = getToolbarControlsGroup6({ toolbarOptions });
  const group7 = getToolbarControlsGroup7({ toolbarOptions });
  const group8 = getToolbarControlsGroup8({ toolbarOptions });

  // As per the design, we do not want a separator between group 1 and the rest of the groups
  // so we handle it independently
  const controlGroups = [
    group2,
    group3,
    group4,
    group5,
    group6,
    group7,
    group8,
  ].filter((group) => group.length > 0);

  return (
    <WrapperItems alignItems="center">
      {group1.length > 0 && <ControlsGroup>{group1}</ControlsGroup>}
      {controlGroups.map((group, index) => (
        <ControlsGroup key={index}>
          {index > 0 && <Separator />}
          {group}
        </ControlsGroup>
      ))}
    </WrapperItems>
  );
};

const Toolbar = forwardRef<HTMLDivElement>((_, ref) => {
  const [focusItem, setFocusItem] = useState(null);

  const handleClickItem = (type) => {
    if (focusItem !== type) {
      setFocusItem(type);
    } else {
      handleCloseItem();
    }
  };

  const handleCloseItem = () => setFocusItem(null);

  // @ts-expect-error useOnClickOutside should accept null
  useOnClickOutside(ref, handleCloseItem);
  useOnEscape(handleCloseItem);

  return (
    <Wrapper ref={ref}>
      <ItemsByOptions handleClickItem={handleClickItem} focusItem={focusItem} />
    </Wrapper>
  );
});

export default Toolbar;

const Wrapper = styled(Box).attrs({
  px: '9px',
  py: 1,
  id: 'toolbar',
})`
  background: ${({ theme }) => theme.input.bg};
  border-radius: 0 0 4px 4px;
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndices.pinned};
  ${separatorShadow}
`;

const WrapperItems = styled(Box)`
  flex-wrap: wrap;
  > * > * + * {
    margin-left: ${(props) => props.theme.space[3]}px;
  }
`;

const ControlsGroup = styled(Box).attrs({
  mr: 3,
})`
  flex: 0 0 auto;
  align-items: center;
`;
